import { Button, Box, Flex } from "@chakra-ui/react"
import type { BaseSelection, GameLogicState } from "@types"
import type React from "react"
import { memo, useCallback, useEffect, useRef, useState } from "react"
import defaultGameData from "../default"
import { GameText } from "../../components/GameText"
import { lookupGameIcon } from "../../components/GameIcon"
import type { TurnPlan } from "../../app/gameLogic"
import { planTurn } from "../../app/gameLogic"
import { useAppDispatch, useAppSelector, useAppStore } from "../../app/hooks"
import {
  applyTurn,
  resetGame,
  selectLastTurnEffects,
  selectLastTurnNonce,
  selectSlide,
} from "./gameSlice"
import { GameSummary } from "./GameSummary"
import { MentorGateModal } from "./MentorGateModal"
import type { WarmedImage } from "./turnTransition"
import { afterNextPaint, onIdle, warmImage, withCap } from "./turnTransition"
import {
  perfDecisionEvent,
  perfDecisionStart,
  perfSwallowedTap,
  usePerfRenderCount,
} from "../../perf/perfTrace"

/**
 * Width the slide picture is laid out at, for choosing a srcset candidate.
 * In portrait the box is taller than wide and object-fit: cover scales by
 * height, so the larger variant is needed there; in landscape (724–836 px
 * box at DPR 1) and on phones the 1024 one is enough.
 */
export const SLIDE_IMAGE_SIZES =
  "(orientation: portrait) 1536px, (min-width: 62em) 836px, 100vw"

/** Taps stay locked this long after the new slide has painted. */
const UNLOCK_DWELL_MS = 250
/** Upper bound on waiting for a picture that is still decoding at tap time. */
const IMAGE_WAIT_MS = 300
/** Pre-rolling starts this long after a slide commits, never competing with it. */
const PREROLL_DELAY_MS = 150

type PreparedTurn = TurnPlan & { image: WarmedImage }
type PreRoll = { forState: GameLogicState; plans: Map<number, PreparedTurn> }

const SlideViewer: React.FC = () => {
  usePerfRenderCount("SlideViewer")
  const dispatch = useAppDispatch()
  const store = useAppStore()
  const currentSlide = useAppSelector(selectSlide)
  const slide = currentSlide ?? defaultGameData.content[0].groups[0].slides[0]

  // Which answer was tapped this turn (null = none): drives the pressed and
  // disabled looks. The ref is the actual lock, so a second tap in the same
  // frame is caught before React has rendered the state.
  const [chosenIndex, setChosenIndex] = useState<number | null>(null)
  const lockRef = useRef(false)
  // A pending mentor gate: the plan is applied once the mentor approves.
  const [gate, setGate] = useState<TurnPlan | null>(null)
  // The next slide (and its decoded picture) for every answer of the
  // current slide, computed while the player reads.
  const preRollRef = useRef<PreRoll | null>(null)

  useEffect(() => {
    if (!slide || slide.selections.length === 0) return
    const idle = onIdle(() => {
      const game = store.getState().game
      const plans = new Map<number, PreparedTurn>()
      slide.selections.forEach((selection, index) => {
        const plan = planTurn(game, selection)
        plans.set(index, {
          ...plan,
          image: warmImage(plan.nextSlide, SLIDE_IMAGE_SIZES),
        })
      })
      preRollRef.current = { forState: game, plans }
    }, PREROLL_DELAY_MS)
    return () => {
      idle.cancel()
      preRollRef.current = null
    }
  }, [slide, store])

  // Once the new slide has painted (plus a short dwell), accept taps again.
  // Counting from the paint rather than the commit means a slow device can
  // never unlock before the player can see the new answers.
  useEffect(() => {
    perfDecisionEvent("commit")
    setGate(null)
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    void afterNextPaint().then(() => {
      if (cancelled) return
      perfDecisionEvent("text-paint")
      timer = setTimeout(() => {
        lockRef.current = false
        setChosenIndex(null)
        perfDecisionEvent("unlock")
      }, UNLOCK_DWELL_MS)
    })
    return () => {
      cancelled = true
      if (timer !== undefined) clearTimeout(timer)
    }
  }, [slide])

  const commitTurn = useCallback(
    async (plan: TurnPlan, image?: WarmedImage) => {
      // Text, meters, feedback and picture land in one commit; the picture is
      // normally decoded already, and a still-decoding one waits briefly
      // rather than leaving the old picture on screen.
      if (image) await withCap(image.ready, IMAGE_WAIT_MS)
      dispatch(
        applyTurn({
          updated: plan.updated,
          slide: plan.nextSlide,
          effects: plan.effects,
        }),
      )
    },
    [dispatch],
  )

  const choose = useCallback(
    async (index: number, event: React.MouseEvent) => {
      if (lockRef.current) {
        perfSwallowedTap()
        return
      }
      lockRef.current = true
      const game = store.getState().game
      const selection = slide.selections[index]
      perfDecisionStart(event.timeStamp, {
        slide: game.slide?.title.en,
        choice: selection.title.en,
        image: game.slide?.image,
        historyLen: game.history.length,
      })
      // 1. Acknowledge: only the answer buttons re-render, and they paint
      //    before any of the turn's work runs.
      setChosenIndex(index)
      await afterNextPaint()
      perfDecisionEvent("ack-paint")
      // 2. Use the pre-rolled plan when the state it was computed for is
      //    still current; otherwise plan now (same logic, just later).
      const preRoll = preRollRef.current
      const prepared =
        preRoll && preRoll.forState === game
          ? preRoll.plans.get(index)
          : undefined
      const plan = prepared ?? planTurn(game, selection)
      if (plan.gatedMeters.length > 0) {
        // Pause for the mentor; the tapped answer stays pressed and locked.
        setGate(prepared ?? plan)
        return
      }
      await commitTurn(plan, prepared?.image)
    },
    [slide, store, commitTurn],
  )

  const approveGate = useCallback(() => {
    if (!gate) return
    setGate(null)
    void commitTurn(gate, (gate as Partial<PreparedTurn>).image)
  }, [gate, commitTurn])

  if (!slide) {
    return null
  }

  // Check if game is over (no more selections available)
  const isGameOver = slide.selections.length === 0

  // Show summary screen if game is over
  if (isGameOver) {
    return <GameSummary />
  }

  return (
    // The slide-* class names hook the phone and upright layouts in
    // src/index.css, which show the whole picture instead of cropping it.
    <Flex className="slide-viewer" direction="column" minH="100%" width="100%">
      {/* Image area: takes whatever height is left after the answer buttons,
          so meters + story + answers share one screen without scrolling.
          The description is in normal flow at the bottom (image absolutely
          positioned behind it), so a long story grows the box instead of
          being clipped — the container's flex-basis is its text height.
          That is the tablet-landscape layout; upright screens and sideways
          phones rearrange it (see index.css). */}
      <Box
        className="slide-frame"
        position="relative"
        width="100%"
        flex="1 1 auto"
        minH={{ base: "min(200px, 30vh)", md: "min(260px, 32vh)" }}
        display="flex"
        flexDirection="column"
        justifyContent="flex-end"
        borderRadius="2xl"
        overflow="hidden"
        bg="linear-gradient(135deg, #fef3c7 0%, #fbbf24 50%, #f59e0b 100%)"
      >
        {/* Keyed per slide: a fresh element never shows the previous slide's
            picture while the new one decodes (the gradient shows instead),
            and the pre-decoded candidate paints in the commit frame. */}
        <img
          key={slide.image}
          className="slide-image"
          src={slide.image}
          srcSet={slide.imageSrcSet}
          sizes={slide.imageSrcSet ? SLIDE_IMAGE_SIZES : undefined}
          alt={slide.imageAlt}
          decoding="async"
          onLoad={() => perfDecisionEvent("image-load")}
          {...{ elementtiming: "slide-image" }}
        />
        {/* Floating flashes for effects on meters not shown in the main UI
            (agency, overall health, relationship) so every point movement
            produces visible feedback regardless of slide content. */}
        <HiddenMeterFlashes />
        {/* Review feedback (7 October 2026): the story text was a bit small
            on the tablet. 19px (+19% on the 16px default), tighter leading so
            the longest stories (4 lines at 800x1280) still leave the picture
            its height. The answer buttons keep the default size. */}
        <Box
          className="slide-story"
          position="relative"
          width="100%"
          bg="rgba(0, 0, 0, 0.75)"
          color="white"
          p={{ base: 3, md: 4 }}
          fontSize="19px"
          lineHeight="1.45"
        >
          <GameText text={slide.description} />
        </Box>
      </Box>
      {/* Answer buttons below the image, full width. Keyed per slide so each
          slide gets fresh buttons: no sticky touch hover or focus carried
          over from the answer just tapped. */}
      <Box
        className="slide-answers"
        width="100%"
        mt={{ base: 3, md: 4 }}
        flexShrink={0}
      >
        <SlideButtonGroup
          key={slide.title.en}
          selections={slide.selections}
          chosenIndex={chosenIndex}
          onChoose={choose}
        />
      </Box>
      {gate && <MentorGateModal plan={gate} onApprove={approveGate} />}
    </Flex>
  )
}

const HIDDEN_METERS = [
  ["agency", "Agency"],
  ["health", "Health"],
  ["relationship", "Relationships"],
] as const

// Chakra green.300 / red.300
const HIDDEN_FLASH_COLOR = { up: "#68d391", down: "#fc8181" }

const HiddenMeterFlashes = memo(function HiddenMeterFlashes() {
  const effects = useAppSelector(selectLastTurnEffects)
  const nonce = useAppSelector(selectLastTurnNonce)
  // Only turns taken after mount flash (never a persisted one on reload)
  const mountNonce = useRef(nonce)
  if (!effects || nonce === mountNonce.current) return null
  const entries = HIDDEN_METERS.map(
    ([key, label]) => [key, label, effects[key] ?? 0] as const,
  ).filter(([, , value]) => value !== 0)
  if (entries.length === 0) return null

  return (
    <Box
      position="absolute"
      top={2}
      right={2}
      zIndex={10}
      pointerEvents="none"
      display="flex"
      flexDirection="column"
      gap={1}
      alignItems="flex-end"
    >
      {entries.map(([key, label, value]) => (
        <span
          key={`${nonce}-${key}`}
          className="hidden-meter-flash"
          style={{
            color: value > 0 ? HIDDEN_FLASH_COLOR.up : HIDDEN_FLASH_COLOR.down,
          }}
        >
          {value > 0 ? "+" : ""}
          {value} {label}
        </span>
      ))}
    </Box>
  )
})

type ChooseHandler = (index: number, event: React.MouseEvent) => void

const SlideButtonGroup = memo(function SlideButtonGroup(props: {
  selections: BaseSelection[]
  chosenIndex: number | null
  onChoose: ChooseHandler
}) {
  // No reserved empty slots: the lock protects against taps landing on a
  // shifted or replaced button, and the freed vertical space is what lets
  // the whole slide fit a small tablet screen. Always one column: the
  // small tablet held sideways (960x600), which used to take the answers
  // two-up, now has them in the half-width column beside the picture
  // (src/index.css), where two-up would not fit.
  return (
    <Box display="grid" gridTemplateColumns="1fr" gap={3} width="100%">
      {props.selections.map((selection, index) => (
        <SlideButton
          key={index}
          index={index}
          action={selection}
          pressed={props.chosenIndex === index}
          locked={props.chosenIndex !== null}
          onChoose={props.onChoose}
        />
      ))}
    </Box>
  )
})

export const ResetButton = () => {
  const dispatch = useAppDispatch()

  const onReset = () => {
    dispatch(resetGame())
  }

  return (
    <Button
      onClick={onReset}
      bg="blue.500"
      color="white"
      _hover={{ bg: "blue.600" }}
      _active={{ bg: "blue.700" }}
      mt="4"
      w="100%"
      size="lg"
      fontWeight="semibold"
    >
      Start Over
    </Button>
  )
}

const SlideButton = memo(function SlideButton(props: {
  index: number
  action: BaseSelection
  /** This is the answer that was tapped this turn */
  pressed: boolean
  /** Some answer was tapped this turn: the others are disabled */
  locked: boolean
  onChoose: ChooseHandler
}) {
  usePerfRenderCount("SlideButton")
  const icon = lookupGameIcon(props.action?.icon)
  const color = "blue" // Single color for all buttons to avoid influencing choices

  return (
    <Box w="100%" h="100%" display="flex">
      {/* Auto-height wrapping button: Chakra's Button defaults to a fixed
          height and nowrap, which made long answers run off the button and
          paint over the one below on narrow screens. No hover/tap scale-up:
          scaled buttons covered their neighbours. flexGrow keeps side-by-side
          buttons equal height. No transition: the pressed look must appear in
          the very next frame after the tap, not fade in over 200 ms. */}
      <Button
        size="lg"
        aria-label="choice"
        leftIcon={icon ?? undefined}
        w="100%"
        colorScheme={color}
        onClick={event => props.onChoose(props.index, event)}
        isDisabled={props.locked && !props.pressed}
        data-pressed={props.pressed ? "true" : undefined}
        h="auto"
        minH="48px"
        py={3}
        whiteSpace="normal"
        textAlign="center"
        flexGrow={1}
        transitionDuration="0s"
        sx={{
          wordBreak: "break-word",
          // Touch screens keep :hover on the last tapped spot; only real
          // pointers get the hover shade.
          "&:hover": { bg: "blue.500" },
          "@media (hover: hover)": { "&:hover": { bg: "blue.600" } },
          "&[data-pressed]": {
            bg: "blue.700",
            transform: "scale(0.98)",
            pointerEvents: "none",
          },
        }}
        _active={{ transform: "scale(0.98)" }}
      >
        <GameText text={props.action.title} />
      </Button>
    </Box>
  )
})

export default SlideViewer
